import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  Steps,
  Button,
  PageHeader,
  notification,
  Space,
  Modal,
  Rate,
  Alert,
  Comment,
  List,
  message,
  Spin,
} from "antd";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { orderDetailsSaga } from "./saga";
import * as selectors from "./selectors";
import ViewGRNForm from "../../../../forms/ViewGRNForm";
import AddGRNForm from "../../../../forms/AddGRNForm";
import EditGRNForm from "../../../../forms/EditGRNForm";
import { useAuth } from "../../../../contexts/auth";
import LabResponse from "../../../../section/LabResponse";
import SampleDetails from "../../../../section/SampleDetails";
import OrderDetails from "../../../../section/OrderDetails";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import requestCorrectionSchema from "../../../../schemas/requestCorrectionForm";
import RequestCorrectionForm from "../../../../forms/RequestCorrectionForm";
import routes from "../../../../../routes";
import { reverse } from "named-urls";
import NotFoundPage from "../../NotFoundPage";
import AssignLabAndLogistic from "./components/AssignLabAndLogistic";
import { dateToString } from "../../../../utils/formatter";
import ViewCorrection from "../../../../components/ViewCorrection";
import HederaModal from "./components/DataVerificationModal/HederaModal";
import ShareReport from "./components/ShareReport";
import OrderCorrection from "./components/OrderCorrection";
import { ViewOrderCorrectionRequest } from "./components/ViewOrderCorrectionRequest";
import { statusLabelToKey } from "../../../../utils/constantData";
import { MarkRegretOrReplace } from "./components/MarkRegretOrReplace";

const { Step } = Steps;

const styles = {
  pinnedContainer: {
    display: "flex",
    flexDirection: "column",
    padding: "20px 20px",
    border: "1px solid #E0E0E0",
    background: "#fff",
    borderRadius: 8,
  },
  metaDataContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "20px 40px",
    border: "1px solid #E0E0E0",
    background: "#eff3f7",
    borderRadius: 8,
    marginBottom: 10,
  },
  progressContainer: {
    padding: "30px 0",
    border: "1px solid #E0E0E0",
    background: "#fff",
    borderRadius: 8,
    marginBottom: 10,
    overflowX: "scroll",
  },
  buttonContainer: {
    display: "flex",
    background: "#fff",
    borderRadius: 8,
    padding: "12px 0px 0px 0px",
    gap: "10px",
  },
  tabNavMenu: {
    display: "flex",
  },
  tabNavMenuItem: {
    padding: "20px 10px 10px 10px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  tabNavMenuItemActive: {
    padding: "20px 10px 10px 10px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderBottom: "3px solid #0a8080",
  },
  reportSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    padding: "30px",
    backgroundColor: "#fff",
    marginLeft: 20,
    borderRadius: 8,
  },
};

const buttonPermissionsMap = {
  pending: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: false,
    createFreshOrder: true,
    moveToNextStatus: true,
    requestOrderCorrection: true,
    viewOrderCorrection: true,
  },
  orderCorrection: {
    viewOrderCorrection: true,
    moveToNextStatus: true,
  },
  booked: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: false,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
  inReview: {
    requestCorrection: true,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: true,
  },
  readyForPickup: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: true,
  },
  outForPickup: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: true,
  },
  deliveredToLab: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
  receivedAtLab: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
  underTesting: {
    requestCorrection: false,
    markAsRegret: false,
    mapPreviousDetails: false,
    createFreshOrder: false,
    moveToNextStatus: false,
  },
  reportUploaded: {
    requestCorrection: false,
    markAsRegret: false,
    mapPreviousDetails: false,
    createFreshOrder: false,
    moveToNextStatus: true,
  },
  completed: {
    requestCorrection: false,
    markAsRegret: false,
    mapPreviousDetails: false,
    createFreshOrder: false,
    moveToNextStatus: false,
  },
  needCorrection: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
  regret: {
    requestCorrection: false,
    markAsRegret: false,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
  notFound: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: false,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
  rejectedByLab: {
    requestCorrection: false,
    markAsRegret: true,
    mapPreviousDetails: true,
    createFreshOrder: true,
    moveToNextStatus: false,
  },
};

const OrdersNavigation = ({ activeNavOption, changeNavMenu }) => {
  return (
    <div style={styles.tabNavMenu}>
      <p
        style={
          activeNavOption === "Order Details"
            ? styles.tabNavMenuItemActive
            : styles.tabNavMenuItem
        }
        onClick={(e) => {
          changeNavMenu("Order Details");
        }}
      >
        Order Details
      </p>
      <p
        style={
          activeNavOption === "Sample Details"
            ? styles.tabNavMenuItemActive
            : styles.tabNavMenuItem
        }
        onClick={(e) => {
          changeNavMenu("Sample Details");
        }}
      >
        Sample Details
      </p>
      <p
        style={
          activeNavOption === "Lab Response"
            ? styles.tabNavMenuItemActive
            : styles.tabNavMenuItem
        }
        onClick={(e) => {
          changeNavMenu("Lab Response");
        }}
      >
        Lab Response
      </p>
    </div>
  );
};

const OrderDetailsPage = () => {
  const [markAsRegretModalVisible, setMarkAsRegretModalVisible] =
    useState(false);
  const [requestCorrectionModalVisible, setRequestCorrectionModalVisible] =
    useState(false);
  const [viewCorrectionModalVisible, setViewCorrectionModalVisible] =
    useState(false);
  const [isEditingPurchaseDetails, setIsEditingPurchaseDetails] =
    useState(false);
  const [isEditingSampleDetails, setIsEditingSampleDetails] = useState(false);
  const [inProgressEditSampleDetails, setInProgressEditSampleDetails] =
    useState(false);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [isLabAndLogisticModalVisible, setIsLabAndLogisticModalVisible] =
    useState(false);

  const { orderId } = useParams();
  const id = orderId;
  const history = useHistory();
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: orderDetailsSaga });
  const dispatch = useDispatch();

  const [activeNavOption, setActiveNavOption] = useState("Order Details");
  const [openViewCorrectionModal, setOpenViewCorrectionModal] = useState(false);
  const [orderCorrectionModalVisible, setOrderCorrectionModalVisible] =
    useState(false);
  const [viewOrderCorrection, setViewOrderCorrection] = useState(false);

  useEffect(() => {
    dispatch(actions.fetchOrderDetails(id));
    dispatch(actions.fetchDropdown(["orderStatus"]));
    dispatch(actions.fetchCompanyFeatures());
    dispatch(actions.fetchCommentsRequest({ query: `_id=${id}` }));

    return () => {
      dispatch(actions.clearOrderSpecificData());
    };
  }, [dispatch, id]);

  const auth = useAuth();
  const user = auth.loggedInUser;

  const userRole = auth.loggedInUser?.role;

  const commodities = useSelector(selectors.selectCommodities) || [];
  const isSubmittingCorrection = useSelector(
    selectors.selectIsSubmittingCorrection
  );
  const states = useSelector(selectors.selectStates) || [];
  const districts = useSelector(selectors.selectDistricts) || [];
  const samplers = useSelector(selectors.selectSamplers) || [];
  const dropdowns = useSelector(selectors.selectDropdowns) || [];
  const projectCodes = useSelector(selectors.selectProjectCodes) || [];
  // const companies = useSelector(selectors.selectCompanies) || [];
  const sectors = useSelector(selectors.selectSectors);

  const isOrderDetailPageLoading = useSelector(
    selectors.selectIsOrderDetailPageLoading
  );
  const orderDetailPageError = useSelector(
    selectors.selectOrderDetailPageError
  );
  // const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  const orderDetails = useSelector(selectors.selectOrderDetails) || {};
  const sampleDetails = useSelector(selectors.selectSampleDetails) || {};
  const checkForGRN = useSelector(selectors.selectCheckForGRN);
  const correctionCommentPresent = useSelector(selectors.selectCommentsSuccess);
  const agencies = useSelector(selectors.selectAgencies) || [];

  const comments = useSelector(selectors.selectComments);
  const { grn, grnFile, company, feedback } = orderDetails;

  const currentStatus = orderDetails?.status?.value;
  const permissions = buttonPermissionsMap[currentStatus] || {};

  const changeNavMenu = (value) => {
    setActiveNavOption(value);
    if (value === "Sample Details") {
      dispatch(actions.fetchSampleDetails(orderDetails.orderId));
    }
  };

  useEffect(() => {
    if (company)
      dispatch(actions.fetchCompanyFeatures({ companyId: company._id }));
  }, [dispatch, company]);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Message",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  const [viewGRNModalVisible, setViewGRNModalVisible] = useState(false);
  const [addGRNModalVisible, setAddGRNModalVisible] = useState(false);
  const [editGRNModal, setEditGRNModal] = useState(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  // const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [markedAsRegret, setMarkedAsRegret] = useState(false);
  const [createdReplacementOrder, setCreatedReplacementOrder] = useState(true);
  const [updateOrderStatusModalVisible, setUpdateOrderStatusModalVisible] =
    useState(false);

  useEffect(() => {
    if (createdReplacementOrder) {
      dispatch(actions.fetchStates());
      dispatch(actions.fetchCommodities());
      // dispatch(actions.fetchCompanies());
      dispatch(actions.fetchSectors());
      dispatch(actions.fetchAgencies());
      return () => {
        dispatch(actions.initializeState());
      };
    }
  }, [createdReplacementOrder, dispatch]);

  const tableData = [];
  const { packages = [] } = orderDetails;
  let dataForKeyParameterValues = [];
  packages.forEach((pack) => {
    tableData.push({
      description: `Package: ${pack.name}`,
      price: `₹ ${pack.cost * pack.sampleCount}`,
    });
  });
  tableData.push({
    description: "Total Package Cost",
    price: `₹ ${orderDetails.total}`,
  });
  tableData.push({
    description: "Discount",
    price: `(-) ${orderDetails.discount}`,
  });
  tableData.push({
    description: "Total Cost",
    price: `₹ ${orderDetails.grandTotal}`,
  });

  const getProgressStatus = () => {
    const { statusTrail } = orderDetails;

    if (!orderDetails?.projectCode && !orderDetails?.projectCodeId) {
      const timelineWithoutDispute = [
        "Pending",
        "Booked",
        "Out for Pickup",
        "Received at Lab",
        "Under Testing",
        "Report Uploaded",
        "Completed",
      ];
      const timelineForRejectedByLab = [
        "Pending",
        "Booked",
        "Out for Pickup",
        "Rejected By Lab",
      ];
      let regretted = false;
      let underDispute = false;
      let currentProgress = statusTrail?.length - 1 || 0;
      let timeline = timelineWithoutDispute;
      let timelineWithTimestamps = [];
      statusTrail?.forEach((obj) => {
        if (obj.status?.value === "underDispute") {
          underDispute = true;
        }
        if (obj.status?.value === "paymentVerified") {
          currentProgress = currentProgress - 1;
        }
        if (obj.status?.value === "regret") {
          regretted = true;
        }
      });
      if (orderDetails?.status?.value === "rejectedByLab") {
        currentProgress = timelineForRejectedByLab.length - 1;
        timeline = timelineForRejectedByLab;
      }
      if (regretted) {
        timeline = statusTrail.map((obj) => obj.status.label);
        currentProgress = statusTrail?.length - 1;
      }
      if (underDispute) {
        timeline = statusTrail.map((obj) => obj.status.label);
        let statusTrailReduced = statusTrail.map((el) => el.status.value);
        if (statusTrailReduced.indexOf("completed") === -1)
          timeline.push("Completed");
        currentProgress = statusTrail?.length - 1;
      }
      timelineWithTimestamps = timeline.map((step, index) => {
        const matchingStatus = statusTrail?.find(
          (obj) => obj.status.label === step
        );
        return {
          title: step,
          timestamp: matchingStatus?.updatedAt
            ? dateToString(matchingStatus.updatedAt)
            : null,
        };
      });
      return { timeline: timelineWithTimestamps, currentProgress };
    }

    const orderStatusFlow =
      orderDetails?.projectCodeId?.projectConfig?.executionScope
        ?.orderStatusFlow || {};
    const newTimeline = [
      "Pending",
      "Booked",
      "In Review",
      "Ready for Pickup",
      "Out for Pickup",
      "Delivered to Lab",
      "Received at Lab",
      "Under Testing",
      "Report Uploaded",
      "Completed",
    ];

    const statusToIndex = {
      pending: 0,
      booked: 1,
      inReview: 2,
      readyForPickup: 3,
      outForPickup: 4,
      deliveredToLab: 5,
      receivedAtLab: 6,
      underTesting: 7,
      reportUploaded: 8,
      completed: 9,
      regret: -1,
      notFound: 1.5,
      needCorrection: 2.5,
      rejectedByLab: 6.5,
    };

    let currentProgress = 0;
    let timeline = [...newTimeline];
    let timelineWithTimestamps = [];

    if (orderDetails?.status?.value === "regret") {
      const statusesBeforeRegret = statusTrail?.filter(
        (status) => status.status.value !== "regret"
      );

      timeline =
        statusesBeforeRegret?.map((status) => status.status.label) || [];
      timeline.push("Regret");

      currentProgress = timeline.length - 1;
    } else {
      const currentStatus = orderDetails?.status?.value;
      currentProgress = statusToIndex[currentStatus] || 0;

      if (currentStatus === "notFound") {
        timeline = ["Pending", "Booked", "Not Found"];
        currentProgress = 2;
      } else if (currentStatus === "orderCorrection") {
        timeline = ["Order Correction", "Pending", "Booked"];
        currentProgress = 0;
      } else if (currentStatus === "needCorrection") {
        timeline = ["Pending", "Booked", "In Review", "Need Correction"];
        currentProgress = 3;
      } else if (currentStatus === "rejectedByLab") {
        timeline = [
          "Pending",
          "Booked",
          "In Review",
          "Ready for Pickup",
          "Out for Pickup",
          "Delivered to Lab",
          "Rejected by Lab",
        ];
        currentProgress = 6;
      } else if (currentStatus === "underDispute") {
        timeline.splice(timeline.length - 1, 0, "Under Dispute");
        currentProgress = timeline.length - 2;
      }
    }

    // Filter out disabled statuses from timeline based on Project Config
    const filteredTimeline = timeline.filter((step) => {
      const statusKey = statusLabelToKey[step];
      return orderStatusFlow[statusKey]?.enabled !== false;
    });

    timelineWithTimestamps = filteredTimeline.map((step) => {
      const matchingStatus = [...(statusTrail || [])]
        .reverse()
        .find((obj) => obj.status.label === step);

      return {
        title: step,
        timestamp: matchingStatus?.updatedAt
          ? dateToString(matchingStatus.updatedAt)
          : null,
      };
    });

    // Recalculate currentProgress based on filtered timeline
    const filteredCurrentProgress = timelineWithTimestamps.findIndex(
      (step) => step.title === orderDetails?.status?.label
    );

    // Use recalculated progress if found, otherwise use original logic
    const adjustedProgress =
      filteredCurrentProgress !== -1
        ? filteredCurrentProgress
        : Math.min(currentProgress, timelineWithTimestamps.length - 1);

    return {
      timeline: timelineWithTimestamps,
      currentProgress: adjustedProgress,
    };
  };

  const handlePurchaseOrderDownload = () => {
    dispatch(
      actions.downloadFile({
        id,
        data: { fileName: orderDetails.purchaseOrderFile },
      })
    );
  };

  const handleReportDownload = () => {
    dispatch(
      actions.downloadFile({
        id,
        data: { fileName: orderDetails.labResponseId.reportFile },
      })
    );
  };

  const handleReportMetadataDownload = () => {
    dispatch(
      actions.downloadFile({
        id,
        data: { fileName: orderDetails.labResponseId.reportMetadataFile },
      })
    );
  };
  const handleGRNFileDownload = () => {
    dispatch(
      actions.downloadFile({
        id,
        data: { fileName: grnFile },
      })
    );
  };

  const handleNewReplacementOrder = () => {
    const newOrderId = orderDetails?.replacementOrder;
    history.push(
      reverse(`${routes.backOffice.orders.details.view}`, {
        orderId: newOrderId,
      })
    );
  };

  const handlePrevRegretButton = () => {
    const prevOrderId = orderDetails?.oldOrder;
    history.push(
      reverse(`${routes.backOffice.orders.details.view}`, {
        orderId: prevOrderId,
      })
    );
  };

  dataForKeyParameterValues = packages[0]?.package?.keyParameters?.map(
    (obj) => {
      let requiredObj = { parameter: obj.parameter };
      if (orderDetails.labResponseId) {
        let valueObj = orderDetails?.labResponseId?.keyParametersValues?.find(
          (el) => el.parameter === obj.parameter
        );
        requiredObj.parameterValue = valueObj?.parameterValue;
      } else {
        requiredObj.parameterValue = "";
      }
      return requiredObj;
    }
  );

  const handleBack = () => {
    history.goBack();
  };

  const handleAddGRN = (payload) => {
    payload.data.id = orderDetails._id;
    payload.data.orderId = orderDetails.orderId;
    payload.data.source = "Order Details";
    payload.data.fileUsedFor = "GRN";
    payload.data.company = orderDetails.user.company._id;
    dispatch(actions.addGRN(payload));
  };

  const handleUpdateGRN = (payload) => {
    payload.data.id = orderDetails._id;
    payload.data.orderId = orderDetails.orderId;
    payload.data.source = "Order Details";
    payload.data.fileUsedFor = "GRN";
    payload.data.company = orderDetails.user.company._id;
    dispatch(actions.updateGRNDetails(payload));
  };
  
  const onRequestCorrectionSubmit = (data) => {
    dispatch(
      actions.requestCorrection({
        orderId: orderDetails.orderId,
        comment: data.detailedInstructions,
        onSuccess: () => {
          setRequestCorrectionModalVisible(false);
          resetRequestCorrectionForm();
          dispatch(actions.fetchCommentsRequest({ query: `_id=${id}` }));
          dispatch(actions.fetchOrderDetails(id));
        },
      })
    );
  };

  const {
    control: requestCorrectionControl,
    handleSubmit: requestCorrectionHandleSubmit,
    watch: requestCorrectionWatch,
    formState: {
      errors: requestCorrectionErrors,
      isValid: isRequestCorrectionValid,
    },
    setValue: requestCorrectionSetValue,
    reset: resetRequestCorrectionForm,
  } = useForm({
    resolver: yupResolver(requestCorrectionSchema),
    defaultValues: {
      detailedInstructions: "",
    },
    mode: "onChange",
  });

  const { currentProgress, timeline } = getProgressStatus();
  const updateOrderStatus = () => {
    const {
      currentProgress,
      timeline,
    } = getProgressStatus();

    if (currentStatus === "regret") {
      notification.warning({
        message: "Cannot update status",
        description: "Order is in regret status",
      });
      return;
    }

    if (currentProgress < timeline.length - 1) {
      const nextStatus = timeline[currentProgress + 1]?.title;

      const statusId = dropdowns.orderStatus.find(
        (el) => el.label === nextStatus
      )?._id;

      if (!statusId) {
        notification.error({
          message: "Failed to update status",
          description: "Status ID not found",
        });
        return;
      }
      setIsStatusChanging(true);
      dispatch(
        actions.updateOrderStatus({
          data: [
            {
              orderId: orderDetails.orderId,
              statusId: statusId,
            },
          ],
          onSuccess: () => {
            notification.success({
              message: "Order status updated successfully",
              placement: "bottomRight",
            });
            setUpdateOrderStatusModalVisible(false);
            setIsStatusChanging(false);
            dispatch(actions.fetchOrderDetails(id));
          },
          onFailure: (error) => {
            setIsStatusChanging(false);
            notification.error({
              message: error?.message || "Failed to update order status",
              placement: "bottomRight",
            });
          },
        })
      );
    } else {
      notification.warning({
        message: "Cannot update status",
        description: "Order is already in the final status",
      });
    }
  };

  const onEditSampleDetailsSubmit = (data) => {
    setInProgressEditSampleDetails(true);
    dispatch(
      actions.updateSampleDetails({
        orderId: orderDetails.orderId,
        data: data,
        onSuccess: () => {
          // notification.success({
          //   message: "Sample details updated successfully",
          // });

          message.success("Sample details updated successfully");
          setIsEditingPurchaseDetails(false);
          setIsEditingSampleDetails(false);
          setInProgressEditSampleDetails(false);
          dispatch(actions.fetchSampleDetails(orderDetails.orderId));
        },
        onFailure: (error) => {
          // notification.error({
          //   message: "Failed to update sample details",
          // });
          message.error({
            content: error?.message || "Failed to update sample details",
            duration: 7, // in seconds
          });
          setInProgressEditSampleDetails(false);
        },
      })
    );
  };

  const LabCorrectionPresent = orderDetails?.underDispute?.length > 0;

  return isOrderDetailPageLoading ? (
    <div
      style={{
        height: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Spin size="large" />
    </div>
  ) : orderDetailPageError ? (
    <NotFoundPage />
  ) : (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title={`Order # ${orderDetails?.orderId ?? ""}`}
        extra={[
          <Space>
            {/* <HederaModal orderId={orderDetails.orderId} /> */}
            {orderDetails?.status?.value === "completed" && (
              <ShareReport orderId={orderDetails.orderId} />
            )}
            {orderDetails.labResponseId?.reportMetadataFile && (
              <Button size="large" onClick={handleReportMetadataDownload}>
                Download Metadata
              </Button>
            )}
            {orderDetails.labResponseId?.reportFile && (
              <Button
                size="large"
                type="primary"
                onClick={handleReportDownload}
              >
                Download Report
              </Button>
            )}
          </Space>,
        ]}
        onBack={handleBack}
      />

      <div style={{ height: "calc(100vh - 145px)", overflowY: "auto" }}>
        {orderDetails?.status?.value === "regret" && (
          <Alert
            message={
              <>
                Order Status is {orderDetails?.status?.label.toUpperCase()},{" "}
                <span style={{ marginLeft: 5 }}>
                  <span style={{ color: "#ED2100" }}>
                    <strong>Reason: </strong>
                  </span>
                  {orderDetails?.orderCancellationType || orderDetails?.orderCancellationReason}
                </span>
              </>
            }
            type="error"
          />
        )}
        {orderDetails?.status?.value === "rejectedByLab" && (
          <Alert
            message={`Order Status is ${orderDetails?.status?.label.toUpperCase()}`}
            type="warning"
          />
        )}
        {orderDetails?.status?.value === "notFound" && (
          <Alert
            message={`Order Status is ${orderDetails?.status?.label.toUpperCase()}`}
            type="info"
          />
        )}
        {orderDetails?.status?.value === "orderCorrection" && (
          <Alert
            message={`Order Status is ${orderDetails?.status?.label.toUpperCase()}`}
            type="warning"
          />
        )}
        <div
          style={{
            padding: "24px 24px 0px 24px",
          }}
        >
          <div style={styles.pinnedContainer}>
            <div style={styles.metaDataContainer}>
              <p
                style={{
                  marginBottom: "0.4em",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Commodity</span>{" "}
                {orderDetails.commodity}
              </p>
              <p
                style={{
                  marginBottom: "0.4em",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Variant</span>{" "}
                {orderDetails.sampleVariant}
              </p>
              <p
                style={{
                  marginBottom: "0.4em",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Type</span> {""}
                {orderDetails.sampleType}
              </p>
              <p
                style={{
                  marginBottom: "0.4em",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>State</span> {""}
                {orderDetails.state}
              </p>
              <p
                style={{
                  marginBottom: "0.4em",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>District</span> {""}
                {orderDetails.district}
              </p>
            </div>

            <div style={styles.progressContainer}>
              <Steps progressDot current={currentProgress} size="small">
                {timeline.map((step, index) => (
                  <Step
                    key={index}
                    title={step?.title}
                    subTitle={step?.timestamp}
                  />
                ))}
              </Steps>
            </div>

            <div style={styles.buttonContainer}>
              {permissions?.moveToNextStatus &&
                (userRole !== "company_admin" || currentStatus === "orderCorrection") &&
                userRole !== "support_team" &&
                userRole !== "csc_admin" && (
                  <Button
                    type="primary"
                    disabled={
                      currentProgress === timeline.length - 1 ||
                      orderDetails?.status?.value === "booked"
                    }
                    onClick={() => {
                      orderDetails?.status?.value === "inReview" &&
                      timeline[currentProgress + 1]?.title ===
                        "Ready for Pickup"
                        ? setIsLabAndLogisticModalVisible(true)
                        : setUpdateOrderStatusModalVisible(true);
                    }}
                  >
                    {currentProgress === timeline.length - 1
                      ? "Completed"
                      : `Move to ${timeline[currentProgress + 1]?.title}`}
                  </Button>
                )}
                {/* Request Order Correction */}
              {permissions?.requestOrderCorrection && (
                <Button
                  danger
                  style={{ backgroundColor: "#ffebee" }}
                  onClick={() => setOrderCorrectionModalVisible(true)}
                >
                  Request Order Correction
                </Button>
              )}
              {/* View Order Correction */}
              {permissions?.viewOrderCorrection && (
                <Button
                  style={{ backgroundColor: "#e6f7ff", borderColor: "#1890ff", color: "#1890ff" }}
                  onClick={() => setViewOrderCorrection(true)}
                >
                  View Order Correction
                </Button>
              )}
              {permissions?.requestCorrection &&
                userRole !== "company_admin" &&
                userRole !== "support_team" &&
                userRole !== "csc_admin" && (
                  <Button
                    onClick={() => setRequestCorrectionModalVisible(true)}
                  >
                    Request Sample Correction
                  </Button>
                )}

              {permissions?.markAsRegret &&
                userRole !== "company_admin" &&
                userRole !== "agnext_reviewer" &&
                userRole !== "support_team" &&
                userRole !== "csc_admin" && (
                  <Button
                    onClick={() => {
                      setMarkAsRegretModalVisible(true);
                    }}
                  >
                    Mark as Regret
                  </Button>
                )}

              {orderDetails?.status?.value === "regret" &&
                orderDetails?.replacementOrder != null && (
                  <Button onClick={() => handleNewReplacementOrder()}>
                    Go to New Replacement Order
                  </Button>
                )}

              {orderDetails?.oldOrder != null && (
                <Button onClick={() => handlePrevRegretButton()}>
                  Go to Regret Order
                </Button>
              )}

              {orderDetails?.status?.value === "regret" &&
                !orderDetails?.replacementOrderCreated &&
                userRole !== "company_admin" &&
                userRole !== "support_team" &&
                userRole !== "agnext_reviewer" &&
                userRole !== "csc_admin" && (
                  <Button
                    onClick={() => {
                      setMarkedAsRegret(false);
                      setCreatedReplacementOrder(true); // Setting it by default when a replacement order is creating for regret order
                      setMarkAsRegretModalVisible(true);
                    }}
                  >
                    Create Replacement Order
                  </Button>
                )}

              {correctionCommentPresent && (
                <Button
                  onClick={() => {
                    setViewCorrectionModalVisible(true);
                  }}
                >
                  View Sample Correction
                </Button>
              )}

              {LabCorrectionPresent && (
                <Button
                  onClick={() => {
                    setOpenViewCorrectionModal(true);
                  }}
                >
                  View Lab Correction
                </Button>
              )}
            </div>
          </div>
        </div>
        <div
          style={{
            padding: "0px 24px 24px 24px",
          }}
        >
          <OrdersNavigation
            activeNavOption={activeNavOption}
            changeNavMenu={changeNavMenu}
          />

          {activeNavOption === "Order Details" && (
            <OrderDetails
              packages={packages}
              orderDetails={orderDetails}
              user={user}
              dataForKeyParameterValues={dataForKeyParameterValues}
            />
          )}

          {activeNavOption === "Sample Details" && (
            <SampleDetails
              sampleDetails={sampleDetails}
              orderDetails={orderDetails}
              onSubmit={onEditSampleDetailsSubmit}
              isEditingPurchaseDetails={isEditingPurchaseDetails}
              setIsEditingPurchaseDetails={setIsEditingPurchaseDetails}
              isEditingSampleDetails={isEditingSampleDetails}
              setIsEditingSampleDetails={setIsEditingSampleDetails}
              inProgressEditSampleDetails={inProgressEditSampleDetails}
              userRole={userRole}
            />
          )}

          {activeNavOption === "Lab Response" && (
            <LabResponse
              packages={packages}
              orderDetails={orderDetails}
              dataForKeyParameterValues={dataForKeyParameterValues}
            />
          )}
        </div>
      </div>

      <ViewGRNForm
        visible={viewGRNModalVisible}
        setVisible={setViewGRNModalVisible}
        grn={grn}
        grnFile={grnFile}
        orderId={orderDetails.orderId}
        handleGRNFileDownload={handleGRNFileDownload}
        setEditGRNModal={setEditGRNModal}
      />
      <AddGRNForm
        visible={addGRNModalVisible}
        setVisible={setAddGRNModalVisible}
        onSave={handleAddGRN}
      />
      <EditGRNForm
        visible={editGRNModal}
        setVisible={setEditGRNModal}
        grn={grn}
        grnFile={grnFile}
        orderId={orderDetails.orderId}
        uploaded={true}
        onSave={handleUpdateGRN}
      />
      <Modal
        title={`Feedback: ${orderDetails?.orderId}`}
        visible={feedbackModalVisible}
        footer={null}
        onCancel={() => {
          setFeedbackModalVisible(false);
        }}
      >
        {" "}
        {feedback ? (
          <div>
            <p>
              <strong>Ratings:</strong>
              <Rate
                defaultValue={feedback?.ratings}
                disabled
                style={{ fontSize: "20px" }}
              />
            </p>
            <div style={{ display: "flex" }}>
              <p>
                <strong>Comments:</strong>
              </p>
              <p style={{ marginLeft: "10px" }}>{feedback?.comments}</p>
            </div>
          </div>
        ) : (
          <p
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {" "}
            No Feedback Yet{" "}
          </p>
        )}
      </Modal>

      {/* marks  order regret or replacement modal */}
      {markAsRegretModalVisible && (
        <MarkRegretOrReplace
          markAsRegretModalVisible={markAsRegretModalVisible}
          setMarkAsRegretModalVisible={setMarkAsRegretModalVisible}
          commodities={commodities}
          states={states}
          districts={districts}
          samplers={samplers}
          agencies={agencies}
          sectors={sectors}
          permissions={permissions}
          orderDetails={orderDetails}
          markedAsRegret={markedAsRegret}
          setMarkedAsRegret={setMarkedAsRegret}
          createdReplacementOrder={createdReplacementOrder}
          setCreatedReplacementOrder={setCreatedReplacementOrder}
        />
      )}
      <Modal
        title="Request Correction"
        visible={requestCorrectionModalVisible}
        onCancel={() => {
          setRequestCorrectionModalVisible(false);
          resetRequestCorrectionForm();
        }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setRequestCorrectionModalVisible(false);
              resetRequestCorrectionForm();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            disabled={!isRequestCorrectionValid}
            loading={isSubmittingCorrection}
            onClick={requestCorrectionHandleSubmit(onRequestCorrectionSubmit)}
          >
            OK
          </Button>,
        ]}
      >
        <div style={{ marginBottom: "20px" }}>
          <p>Write a Comment for Request Correction</p>
        </div>
        <RequestCorrectionForm
          control={requestCorrectionControl}
          errors={requestCorrectionErrors}
          watch={requestCorrectionWatch}
          setValue={requestCorrectionSetValue}
        />
      </Modal>
      <Modal
        title="Update Order Status"
        visible={updateOrderStatusModalVisible}
        onCancel={() => {
          setUpdateOrderStatusModalVisible(false);
        }}
        footer={[
          <Button
            key="back"
            onClick={() => setUpdateOrderStatusModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={updateOrderStatus}
            loading={isStatusChanging}
          >
            Confirm
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to update the order status to{" "}
          <span style={{ color: "#1890ff", fontWeight: "500" }}>
            {timeline[currentProgress + 1]?.title}?
          </span>{" "}
        </p>
        {currentStatus === "orderCorrection" && (
          <Alert
            message="Note: This order is currently under correction. Please ensure that all corrections are addressed before moving to the next status."
            type="warning"
          />
        )}
      </Modal>

      <Modal
        title="Corrections"
        visible={viewCorrectionModalVisible}
        onCancel={() => setViewCorrectionModalVisible(false)}
        footer={[
          <Button
            key="back"
            onClick={() => setViewCorrectionModalVisible(false)}
          >
            Cancel
          </Button>,
        ]}
      >
        {comments && comments.length > 0 ? (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <List
              dataSource={comments}
              itemLayout="horizontal"
              renderItem={(item) => (
                <li>
                  <Comment
                    content={
                      <p style={{ whiteSpace: "pre-wrap" }}>{item.comment}</p>
                    }
                    datetime={
                      item.date ? new Date(item.date).toLocaleString() : ""
                    }
                  />
                </li>
              )}
            />
          </div>
        ) : (
          <div>No comments found.</div>
        )}
      </Modal>

      {/* Assign lab and logistics to the order before updating the status to Out for Pickup. */}
      {orderDetails?.status?.value === "inReview" && (
        <AssignLabAndLogistic
          orderDetails={orderDetails}
          isLabAndLogisticModalVisible={isLabAndLogisticModalVisible}
          setIsLabAndLogisticModalVisible={setIsLabAndLogisticModalVisible}
          setUpdateOrderStatusModalVisible={setUpdateOrderStatusModalVisible}
        />
      )}

      {openViewCorrectionModal && (
        <ViewCorrection
          id={id}
          orderId={orderDetails.orderId}
          onOpen={openViewCorrectionModal}
          onClose={() => setOpenViewCorrectionModal(false)}
        />
      )}

      {/* Request Order Correction Form */}
      <OrderCorrection 
        open={orderCorrectionModalVisible} 
        onClose={() => setOrderCorrectionModalVisible(false)}
        orderId={orderDetails.orderId}
        order_id={orderDetails._id}
      />
      {/* view order correction data  */}
      <ViewOrderCorrectionRequest
        open={viewOrderCorrection}
        onClose={() => setViewOrderCorrection(false)}
        OrderCorrectionData={orderDetails?.orderCorrection}
      />
    </div>
  );
};

export default OrderDetailsPage;
